import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { Lock, Save, ShieldCheck, Fingerprint, Key, Eye, EyeOff } from 'lucide-react';
import './AdminSettings.css';
const API_URL = 'https://api.sohanihandicrafts.com:5010';
const AdminSettings = () => {
    const [passwords, setPasswords] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPass, setShowPass] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const handleChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (passwords.newPassword !== passwords.confirmPassword) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: 'New passwords do not match!' });
            return;
        }

        setIsUpdating(true);
        try {
            const token = localStorage.getItem('adminToken');
            const config = { headers: { 'x-auth-token': token } };

            await axios.put(`${API_URL}/api/auth/change-password`, {
                oldPassword: passwords.oldPassword,
                newPassword: passwords.newPassword
            }, config);

            Swal.fire({ icon: 'success', title: 'Security Updated', text: 'Your password has been changed successfully.', timer: 2000 });
            setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.msg || 'Update failed' });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="stg-wrapper">
            <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }}
                className="stg-container"
            >
                {/* --- LEFT SIDE: ARTISAN BRANDING & INFO --- */}
                <div className="stg-visual-pane">
                    <div className="stg-visual-content">
                        <div className="stg-icon-ring">
                            <Fingerprint size={48} strokeWidth={1} />
                        </div>
                        <span className="stg-overline">Account Security</span>
                        <h2>Guardian of the <br/><span>Artisan Heritage</span></h2>
                        <p>
                            Ensure your administrative portal remains secure. We recommend changing 
                            your master key every 90 days to protect the Sohani Handicrafts database.
                        </p>
                        <div className="stg-features">
                            <div className="stg-f-item"><ShieldCheck size={18}/> <span>Encrypted Vault</span></div>
                            <div className="stg-f-item"><Key size={18}/> <span>JWT Authentication</span></div>
                        </div>
                    </div>
                    {/* Artistic wood-ring background element */}
                    <div className="stg-wood-deco"></div>
                </div>

                {/* --- RIGHT SIDE: PREMIUM FORM --- */}
                <div className="stg-form-pane">
                    <div className="stg-form-header">
                        <h3>Update Credentials</h3>
                        <button 
                            type="button" 
                            className="stg-visibility-toggle" 
                            onClick={() => setShowPass(!showPass)}
                        >
                            {showPass ? <EyeOff size={18}/> : <Eye size={18}/>} 
                            {showPass ? 'Hide' : 'Show'} Passwords
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="stg-form">
                        <div className="stg-input-box">
                            <label>Current Master Password</label>
                            <div className="stg-field">
                                <Lock className="stg-field-icon" size={18} />
                                <input 
                                    type={showPass ? "text" : "password"}
                                    name="oldPassword"
                                    placeholder="Enter current password"
                                    value={passwords.oldPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="stg-divider"></div>

                        <div className="stg-input-box">
                            <label>New Password</label>
                            <div className="stg-field">
                                <Key className="stg-field-icon" size={18} />
                                <input 
                                    type={showPass ? "text" : "password"}
                                    name="newPassword"
                                    placeholder="Create new password"
                                    value={passwords.newPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="stg-input-box">
                            <label>Confirm New Password</label>
                            <div className="stg-field">
                                <ShieldCheck className="stg-field-icon" size={18} />
                                <input 
                                    type={showPass ? "text" : "password"}
                                    name="confirmPassword"
                                    placeholder="Confirm new password"
                                    value={passwords.confirmPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <button type="submit" className="stg-submit-btn" disabled={isUpdating}>
                            {isUpdating ? "Processing..." : "Secure Account"}
                            {!isUpdating && <Save size={18} />}
                        </button>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default AdminSettings;